import React, { useEffect, useState } from "react";

// mui components
import { Box, Modal, Radio, Stack } from "@mui/material";

// custom components
import PrimaryButton from "../../buttons/primary-button";
import SecondaryButton from "../../buttons/secondary-button";
import Heading5 from "../../typography/heading-05";
import Heading6 from "../../typography/heading-06";
import Body2 from "../../typography/body-02";
import Body3 from "../../typography/body-03";

// mui icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";

// helper utils
import {
	DIRECT_PATH_CONSTANTS,
	DIRECT_PATH_OPTIONS,
} from "../../../utils/direct-path.constants";

// custom styles
import styles from "./index.module.scss";

// Shown before the manual application screens so the Commercial Broker picks a
// processing method while the loan number does not exist yet.
export default function DirectPathSelectionModal({
	open,
	organizationName,
	handleClose,
	handleConfirm,
}) {
	const [selectedOption, setSelectedOption] = useState("");

	useEffect(() => {
		if (open) {
			setSelectedOption("");
		}
	}, [open]);

	const options = [
		{
			value: DIRECT_PATH_OPTIONS.DIRECT_PATH,
			title: DIRECT_PATH_CONSTANTS.DIRECT_PATH_OPTION_TITLE,
			helper: DIRECT_PATH_CONSTANTS.DIRECT_PATH_OPTION_HELPER,
			Icon: RouteOutlinedIcon,
			iconClass: styles.directPathOptionIcon,
		},
		{
			value: DIRECT_PATH_OPTIONS.STANDARD,
			title: DIRECT_PATH_CONSTANTS.STANDARD_OPTION_TITLE,
			helper: DIRECT_PATH_CONSTANTS.STANDARD_OPTION_HELPER,
			Icon: AccountBalanceOutlinedIcon,
			iconClass: styles.standardOptionIcon,
		},
	];

	const handleContinue = () => {
		handleConfirm(selectedOption === DIRECT_PATH_OPTIONS.DIRECT_PATH);
	};

	return (
		<Modal
			open={open}
			aria-labelledby="direct path selection modal"
			aria-describedby="direct path selection modal"
			disableAutoFocus={true}
			disableEscapeKeyDown={true}
		>
			<Stack direction="column" className={styles.modalBodyContainer}>
				<Stack direction="row" className={styles.modalHeaderContainer}>
					<RouteOutlinedIcon className={styles.headerIcon} />
					<Stack direction="column" className={styles.headerTextContainer}>
						<Heading5
							text={DIRECT_PATH_CONSTANTS.MODAL_TITLE}
							fontType="semibold"
							extraClass={styles.headerText}
						/>
						{organizationName && (
							<Body3
								text={`${organizationName} ${DIRECT_PATH_CONSTANTS.MODAL_ELIGIBLE_SUFFIX}`}
								extraClass={styles.headerSubText}
							/>
						)}
					</Stack>
				</Stack>

				<Stack direction="column" className={styles.modalContentContainer}>
					<Body2
						text={DIRECT_PATH_CONSTANTS.MODAL_HELPER}
						extraClass={styles.helperText}
					/>

					{options?.map((option) => (
						<Stack
							key={option?.value}
							direction="row"
							className={
								selectedOption === option?.value
									? `${styles.optionContainer} ${styles.selectedOptionContainer}`
									: styles.optionContainer
							}
							onClick={() => setSelectedOption(option?.value)}
						>
							<Radio
								checked={selectedOption === option?.value}
								value={option?.value}
								size="small"
								onChange={() => setSelectedOption(option?.value)}
								inputProps={{ "aria-label": option?.title }}
							/>
							{option?.Icon && (
								<option.Icon className={option.iconClass} />
							)}
							<Stack direction="column" className={styles.optionTextContainer}>
								<Heading6 text={option?.title} fontType="semibold" />
								<Body3
									text={option?.helper}
									extraClass={styles.optionHelperText}
								/>
							</Stack>
						</Stack>
					))}

					<Body3
						text={DIRECT_PATH_CONSTANTS.FEE_TEXT}
						fontType="semibold"
						extraClass={styles.feeText}
					/>

				</Stack>

				<Stack direction="row" className={styles.footerBtnContainer}>
					<Box className={styles.footerBtn}>
						<SecondaryButton
							text={DIRECT_PATH_CONSTANTS.CANCEL_BUTTON}
							onClick={handleClose}
							fullWidth={true}
						/>
					</Box>
					<Box className={styles.footerBtn}>
						<PrimaryButton
							text={DIRECT_PATH_CONSTANTS.CONTINUE_BUTTON}
							endIcon={<ArrowForwardIcon sx={{ fontSize: "18px" }} />}
							onClick={handleContinue}
							disabled={selectedOption === ""}
							fullWidth={true}
						/>
					</Box>
				</Stack>

				{selectedOption === DIRECT_PATH_OPTIONS.DIRECT_PATH && (
					<Body3
						text={DIRECT_PATH_CONSTANTS.POST_SELECTION_NOTICE}
						extraClass={styles.footerNoticeText}
					/>
				)}				
			</Stack>
		</Modal>
	);
}
